import { scry, scryImageUrl } from "../lib/scryClient";
import ScrySearchInput from "./ScrySearchInput";
import TradeRow from "./TradeRow";
import SwipeRow from "./SwipeRow";
import type { TradeItem, TradeSide } from "./tradeTypes";
import { computeTotal, formatCurrency, mergeTradeItems } from "./tradeHelpers";
import React from "react";

type Props = {
  side: TradeSide;
  title: string;
  items: TradeItem[];
  setItems(next: TradeItem[]): void;
  actions?: React.ReactNode; // future: "Add from my collection", "Pick user"
  onItemClick?: (item: TradeItem, index: number) => void;
};

export default function TradePane(props: {
  side: "sent" | "received";
  title: string;
  items: TradeItem[];
  setItems: (fn: (prev: TradeItem[]) => TradeItem[]) => void;
  actions?: React.ReactNode;
  onItemClick?: (item: TradeItem, index: number) => void;
  hideSearch?: boolean;
}) {
  const { items, setItems, onItemClick } = props;
  const total = computeTotal(items);

  // Desktop detection to disable SwipeRow (avoid extra delete affordance)
  function useMediaQuery(query: string) {
    const [matches, setMatches] = React.useState<boolean>(() =>
      typeof window !== "undefined" ? window.matchMedia(query).matches : false,
    );
    React.useEffect(() => {
      if (typeof window === "undefined") return;
      const mql = window.matchMedia(query);
      const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
      // set initial
      setMatches(mql.matches);
      mql.addEventListener?.("change", handler);
      // Safari fallback
      // @ts-ignore
      mql.addListener && mql.addListener(handler);
      return () => {
        mql.removeEventListener?.("change", handler);
        // @ts-ignore
        mql.removeListener && mql.removeListener(handler);
      };
    }, [query]);
    return matches;
  }
  const isDesktop = useMediaQuery("(min-width: 768px)");

  function handlePick(it: TradeItem) {
    setItems(mergeTradeItems(items, [{ ...it, qty: Math.max(1, Math.floor(it.qty || 1)) }]));
  }

  function onChangeQty(key: string, qty: number) {
    setItems(items.map((i) => (i.key === key ? { ...i, qty } : i)));
  }

  function onRemove(key: string) {
    setItems(items.filter((i) => i.key !== key));
  }

  const removeByKey = (k: string) => setItems((prev) => prev.filter((p) => p.key !== k));

  // Add near the top-level helpers
  function scryfallImgFallback(scryId?: string | null) {
    return scryId
      ? ` + scryImageUrl(scryId, "normal") + `
      : "/images/cardback.png";
  }

  return (
    <div className="flex h-full flex-col gap-3 rounded-md border border-gray-800 bg-black/30 p-3">
      <div className="flex items-center justify-between">
        <div className="text-lg font-medium text-gray-100">{props.title}</div>
        <div className="text-sm text-gray-300">Total: {formatCurrency(total)}</div>
      </div>

      <div className="flex flex-col gap-2">
        {!props.hideSearch && (
          <ScrySearchInput
            placeholder={props.side === "sent" ? "Add card to send (Scryfall search)..." : "Add card to receive (Scryfall search)..."}
            onPick={handlePick}
          />
        )}
        {props.actions}
      </div>

      <div className="flex-1 overflow-auto">
        <div className="flex flex-col gap-2">
          {items.length === 0 ? (
            <div className="rounded border border-gray-800 bg-black/20 p-6 text-center text-sm text-gray-400">
              No cards yet. Use search above to add.
            </div>
          ) : (
            items.map((it, idx) => {
              const img = it.image || scryfallImgFallback(it.scryfall_id);
              const isFromCollection = Boolean(it.ownerUid && it.docId);
              const canPickVersion = !isFromCollection; // searched/free item

              const RowBody = (
                <div
                  key={it.key}
                  className="w-full rounded border border-gray-700 bg-gray-900/40 p-2"
                  onClick={() => canPickVersion && onItemClick?.(it, idx)}
                  role={canPickVersion ? "button" : undefined}
                  tabIndex={canPickVersion ? 0 : -1}
                >
                  {/* Make children stretch to match the tallest (image) so the X button fills height */}
                  <div className="flex items-stretch gap-3">
                    {/* Bigger thumbnail (about 2×) */}
                    <div className="shrink-0">
                      <div className="w-28 sm:w-32 md:w-28 aspect-[3/4] overflow-hidden rounded-md border border-slate-700 bg-black">
                        <img src={img} alt={it.name} className="h-full w-full object-cover" loading="lazy" />
                      </div>
                    </div>

                    {/* Details */}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white font-medium truncate">{it.name}</div>
                      <div className="text-[12px] text-slate-400 truncate">
                        {(it.set || "")?.toString()?.toUpperCase?.() || ""}
                        {it.number ? ` · #${it.number}` : ""}
                        {it.foil ? " · Foil" : ""}
                      </div>
                      {/* ...existing meta (qty/price/etc.)... */}
                    </div>

                    {/* Tall Delete button (desktop only) */}
                    <button
                      className="hidden md:flex h-full min-w-[36px] w-10 items-center justify-center rounded border border-red-700 bg-red-600/80 text-white hover:bg-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeByKey(it.key);
                      }}
                      aria-label={`Delete ${it.name}`}
                      title="Delete"
                    >
                      X
                    </button>
                  </div>
                </div>
              );

              // On desktop: no SwipeRow (avoid extra delete UI), full-width row with tall X
              // On mobile: keep SwipeRow for swipe-to-delete, hide inline delete via CSS
              return isDesktop ? (
                <div key={it.key} className="w-full">
                  {RowBody}
                </div>
              ) : (
                <SwipeRow key={it.key} onDelete={() => removeByKey(it.key)}>
                  {/* Inline X hidden on mobile by CSS (hidden md:flex) */}
                  {RowBody}
                </SwipeRow>
              );
            })
          )}
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between">
        <button
          className="rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-200 hover:bg-white/5"
          onClick={() => setItems([])}
        >
          Clear {props.title}
        </button>
      </div>
    </div>
  );
}

